import { useEffect, useState } from 'react';
import { toDataURL } from 'qrcode';
import { totpConfirm, totpSetup, updateAdminIdentity, type AdminIdentity } from './api';

/**
 * TOTP enrollment (docs/09 §3): fetch a pending secret + otpauth URL, render it
 * as a QR for the authenticator app, then confirm the first code. The secret is
 * only persisted server-side after a successful confirm.
 */
export function TwoFactorPanel({
  onClose,
  onEnrolled,
}: {
  onClose: () => void;
  onEnrolled: (admin: AdminIdentity) => void;
}) {
  const [secret, setSecret] = useState<string | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    totpSetup().then(
      async (r) => {
        if (!alive) return;
        setSecret(r.secret);
        try {
          const dataUrl = await toDataURL(r.otpauthUrl, { width: 220, margin: 1 });
          if (alive) setQr(dataUrl);
        } catch {
          /* QR render failed — manual secret entry still works */
        }
      },
      (e: unknown) => alive && setError(e instanceof Error ? e.message : 'Setup failed.'),
    );
    return () => {
      alive = false;
    };
  }, []);

  const confirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await totpConfirm(code.trim());
      const next = updateAdminIdentity({ totpEnrolled: true });
      if (next) onEnrolled(next);
      else onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Confirmation failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="overlay" onClick={onClose}>
      <form className="gate-card panel" onClick={(e) => e.stopPropagation()} onSubmit={confirm}>
        <h1>
          Enable 2FA<span className="dot">.</span>
        </h1>
        {error && <p className="error-line">{error}</p>}
        {!secret ? (
          <p className="gate-foot">Generating secret…</p>
        ) : (
          <>
            <p className="gate-foot">
              Scan the QR with Google Authenticator / 1Password / Aegis, or enter the secret
              manually. Then type the 6-digit code to confirm. The setup expires after 10 minutes.
            </p>
            {qr && <img className="qr" src={qr} alt="TOTP enrollment QR code" />}
            <p className="mono secret-line">{secret}</p>
            <input
              className="input"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="6-digit code"
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
            <button type="submit" className="gate-submit" disabled={busy || code.trim().length < 6}>
              {busy ? 'Confirming…' : 'Confirm & enable'}
            </button>
          </>
        )}
        <button type="button" className="link-btn" onClick={onClose}>
          Cancel
        </button>
      </form>
    </div>
  );
}
